/**
 * The multilingual SEO pages: every slug, its page type and, for the
 * language pages, the language. Plain data and no content, so App.js can
 * route them all without pulling their JSON into the main bundle (the content
 * loads with the lazy page chunk, catalog.js).
 *
 * CommonJS on purpose: scripts/routes.js (prerender) and
 * scripts/gates/multilingual-content.js read this from Node.
 *
 * A slug is the page's path without the leading "/". Its content file is
 * ./<slug>.json. Every slug is ROUTED (App.js) whether or not it is
 * published: an unpublished page must render the 404 page, and without a
 * route of its own a /learn/... slug would fall through to LearnPage, which
 * redirects to the homepage.
 */

const HUB = "features/multilingual-automation";
const MAIN_RANKING = "learn/best-multilingual-instagram-dm-automation-tools";

const LEARN = [
  "learn/instagram-auto-dm-kaise-kare",
  "learn/how-to-auto-reply-instagram-comments-in-different-languages",
  "learn/can-instagram-auto-dm-work-in-hindi",
  "learn/what-is-multilingual-comment-to-dm",
  "learn/can-one-automation-reply-in-multiple-languages",
  "learn/best-comment-keywords-in-hindi-and-hinglish",
  "learn/hindi-or-hinglish-dm-which-should-you-use",
  "learn/reach-regional-language-audience-on-instagram",
];

/**
 * key: the slug suffix. lang: the BCP 47 tag of the language, used on its
 * native-script text (nativeScript.js). Hindi and Marathi share Devanagari,
 * so on a Marathi page a Devanagari run is "mr"; everywhere else it is "hi".
 */
const LANGUAGES = [
  { key: "hindi", lang: "hi" },
  { key: "hinglish", lang: "hi-Latn" },
  { key: "marathi", lang: "mr" },
  { key: "gujarati", lang: "gu" },
  { key: "tamil", lang: "ta" },
  { key: "telugu", lang: "te" },
  { key: "kannada", lang: "kn" },
  { key: "malayalam", lang: "ml" },
  { key: "spanish", lang: "es" },
  { key: "french", lang: "fr" },
  { key: "german", lang: "de" },
  { key: "turkish", lang: "tr" },
  { key: "japanese", lang: "ja" },
  { key: "korean", lang: "ko" },
];

const languagePageSlug = (key) => `${HUB}/${key}`;
const rankingPageSlug = (key) => `learn/best-instagram-auto-dm-tools-for-${key}`;

/** slug -> { type, language?, native? } — the type each file MUST declare;
 *  native is set on the native-language pages below. */
const PAGE_TYPES = {};
PAGE_TYPES[HUB] = { type: "hub" };
PAGE_TYPES[MAIN_RANKING] = { type: "ranking_main" };
for (const s of LEARN) PAGE_TYPES[s] = { type: "learn" };
for (const l of LANGUAGES) {
  PAGE_TYPES[languagePageSlug(l.key)] = { type: "language", language: l };
  PAGE_TYPES[rankingPageSlug(l.key)] = { type: "ranking_language", language: l };
}

/**
 * Native-language pages, written in their own language end to end and
 * served at /<prefix>/<slug>. prefix -> lang: the BCP 47 tag the file's
 * "lang" field must say, and the page's <html lang>. The prefix is the
 * language code, except Hinglish (hi-Latn), which has a word of its own.
 */
const NATIVE_PREFIXES = {
  hi: "hi", hinglish: "hi-Latn", mr: "mr", gu: "gu", ta: "ta", te: "te", kn: "kn", ml: "ml",
  es: "es", fr: "fr", de: "de", tr: "tr", ja: "ja", ko: "ko",
};

/** One entry per content file under a prefix directory — the content-files
 *  test fails if a file and this list disagree. Per language: the language
 *  page, its ranking, then its how-to (Hinglish has no how-to). */
const NATIVE = [
  ["hi/instagram-auto-dm", "language"],
  ["hi/best-instagram-auto-dm-tools", "ranking_language"],
  ["hi/instagram-auto-dm-kaise-lagaye", "learn"],
  ["hinglish/instagram-auto-dm", "language"],
  ["hinglish/best-instagram-automation-tools", "ranking_language"],
  ["mr/instagram-auto-dm", "language"],
  ["mr/best-instagram-automation-tools", "ranking_language"],
  ["mr/instagram-auto-dm-kasa-lavaycha", "learn"],
  ["gu/instagram-auto-dm", "language"],
  ["gu/best-instagram-automation-tools", "ranking_language"],
  ["gu/instagram-auto-dm-kevi-rite", "learn"],
  ["ta/instagram-auto-dm", "language"],
  ["ta/best-instagram-auto-dm-tools", "ranking_language"],
  ["ta/instagram-auto-dm-eppadi", "learn"],
  ["te/instagram-auto-dm", "language"],
  ["te/best-instagram-automation-tools", "ranking_language"],
  ["te/instagram-auto-dm-ela", "learn"],
  ["kn/instagram-auto-dm", "language"],
  ["kn/best-instagram-automation-tools", "ranking_language"],
  ["kn/instagram-auto-dm-hege", "learn"],
  ["ml/instagram-auto-dm", "language"],
  ["ml/best-instagram-automation-tools", "ranking_language"],
  ["ml/instagram-auto-dm-engane", "learn"],
  ["es/dm-automaticos-instagram", "language"],
  ["es/mejores-herramientas-dm-instagram", "ranking_language"],
  ["es/como-enviar-dm-automaticos", "learn"],
  ["fr/dm-automatiques-instagram", "language"],
  ["fr/meilleurs-outils-automatisation-instagram", "ranking_language"],
  ["fr/comment-envoyer-dm-automatiques", "learn"],
  ["de/automatische-instagram-dms", "language"],
  ["de/beste-instagram-automatisierung-tools", "ranking_language"],
  ["de/automatische-dms-einrichten", "learn"],
  ["tr/instagram-otomatik-dm", "language"],
  ["tr/en-iyi-instagram-otomasyon-araclari", "ranking_language"],
  ["tr/otomatik-dm-nasil-kurulur", "learn"],
  ["ja/instagram-auto-dm", "language"],
  ["ja/best-instagram-automation-tools", "ranking_language"],
  ["ja/auto-dm-setup", "learn"],
  ["ko/instagram-auto-dm", "language"],
  ["ko/best-instagram-automation-tools", "ranking_language"],
  ["ko/auto-dm-setup", "learn"],
].map(([slug, type]) => ({ slug, type, lang: NATIVE_PREFIXES[slug.split("/")[0]] }));
for (const n of NATIVE) PAGE_TYPES[n.slug] = { type: n.type, native: n.lang };

/** Every slug, in the order the hub lists them. */
const SLUGS = Object.keys(PAGE_TYPES);

module.exports = {
  HUB,
  MAIN_RANKING,
  LEARN,
  LANGUAGES,
  NATIVE_PREFIXES,
  NATIVE,
  PAGE_TYPES,
  SLUGS,
  languagePageSlug,
  rankingPageSlug,
};
